import { useState, useMemo, useEffect, Fragment } from 'react';
import { useSelector } from 'react-redux';
import throttle from 'lodash.throttle';

import { 
    Autocomplete,
    TextField,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Chip,
    Typography,
    Card,
    CircularProgress,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import Article  from '@mui/icons-material/Article';
import Message from '@mui/icons-material/Message';

import {
    blue
} from '@mui/material/colors';

const throttleTime = 1000; // in ms

const HomePageAutocomplete = ({ handleValueChange }) => {
    const token = useSelector((state) => state.auth.token);

    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const handleAutocompleteChange = async (event, value) => {
        let query = event.target.value;
        
        if (!query) {
            query = "";
        }
        if (query.length > 0) {
            query = query.trim();
        }
        //setIsTyping(query.length > 0); // exclusively for the loading circular progress wheel
    
        if (query !== "") {
            // clear old options and create array for new options
            setIsLoading(true);
            setOptions([]);
            const newOptions = [];
            // fetch new options
            try {
                {/* GET RECENT PEOPLE */}
                const peopleUrl = new URL(`${process.env.REACT_APP_PORT}/people/searchPeople`);
                peopleUrl.searchParams.set("query", query);
                const peopleResponse = await fetch(peopleUrl, {
                    method: "GET",
                    headers: { Authorization: `Bearer ${token}` },
                });
                let peopleData = await peopleResponse.json();
        
                console.log("people data: ", peopleData);
                if (peopleResponse.status === 200) {
                    peopleData.results = peopleData?.results
                        ?.filter((result) => result?.email?.includes(query))
                        ?.map((result) => { return { ...result, kind: 'people' } });
                    newOptions.push(...(peopleData?.results));
                    // setOptions(data.results);
                } else {
                    console.log("error getting people");
                }

                {/* GET MESSAGES --> EMAILS */}
                const emailAddresses = peopleData?.results?.map((person) => person?.email);
                const messagesUrl = new URL(`${process.env.REACT_APP_PORT}/people/getUserEmails`);
                const messagesResponse = await fetch(messagesUrl, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ emailAddresses })
                });
                
                let messagesData = await messagesResponse.json();
                if (messagesResponse.status === 200) {
                    messagesData.emails = messagesData?.emails
                        ?.filter((message) => message.title.includes(query))
                        ?.map((message) => { return { ...message, kind: 'message' } });
                    newOptions.push(...(messagesData?.emails));
                } else {
                    console.log("error getting emails");
                }

                {/* GET UPLOADED FILES */}
                const filesUrl = new URL(`${process.env.REACT_APP_PORT}/notes/getNotes`);
                const filesResponse = await fetch(filesUrl, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` }
                });

                let filesData = await filesResponse.json();

                if (filesResponse.status === 200) {
                    filesData.notes = filesData?.notes
                        ?.filter((result) => result.name.includes(query))
                        ?.map((result) => { return { ...result, kind: 'file' } });
                    newOptions.push(...(filesData?.notes));
                } else {
                    console.log('error getting notes');
                }

                {/* SET NEW OPTIONS */}
                console.log("new options are", newOptions);
                setOptions(newOptions);
                setIsLoading(false);
            } catch (error) {
                console.log('error is', error);
                setOptions([]);
                setIsLoading(false);
            }
        }
    };

    const throttledHandleAutocompleteChange = useMemo(
        () => throttle(handleAutocompleteChange, throttleTime)
    , []);

    // Stop the invocation of the throttled function after unmounting
    useEffect(() => {
		return () => {
			throttledHandleAutocompleteChange.cancel();
		};
	}, []);

    return (
        <Autocomplete 
            multiple={false}
            componentsProps={{ popper: { placement: 'bottom-start', style: { width: 300 } } }}
            filterOptions={(x) => x}
            onInputChange={throttledHandleAutocompleteChange}
            options={options}
            getOptionLabel={(option) => 
                (option.kind === 'people' ? option.email : 
                option.kind === 'message' ? option.title :
                option.kind === 'file' ? option.name : 
                'undefined')
            }
            fullWidth
            loading={isLoading}
            onChange={handleValueChange}
            renderInput={(params) => (
                <Card
                    raised
                    sx={{
                        borderRadius: '50px',
                        bgcolor: 'transparent'
                    }}
                >
                    <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Search across all your files, messages, and people..."
                        sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '50px',
                                backgroundColor: 'white',
                            },
                        }}
                        InputProps={{
                            ...params.InputProps,
                            fullWidth: true,
                            startAdornment: (
                            <SearchIcon
                                sx={{
                                    marginRight: '8px',
                                    marginLeft: '8px',
                                    color: 'gray',
                                    size: 'large',
                                }}
                            />
                            ),
                            endAdornment: (
                                <Fragment>
                                    {isLoading && <CircularProgress color="inherit" size={20} />}
                                </Fragment>
                            )
                        }}
                    />
                </Card>
            )}
            renderOption={(props, option) => (
                <ListItem {...props} key={
                    option.kind === 'people' ? option.resourceName :
                    option.kind === 'message' ? option.id :
                    option.kind === 'file' ? option.thirdPartyId :
                    null
                } dense>
                    <ListItemAvatar >
                        {option.kind === 'people' && (
                            <Avatar 
                                src={option.photo} 
                                sx={{ width: 34, height: 34 }}
                            />
                        )}
                        {option.kind === 'message' && (
                            <Avatar sx={{ width: 34, height: 34, bgcolor: blue[700] }}>
                                <Message />   
                            </Avatar>
                        )}
                        {option.kind === 'file' && (
                            <Avatar sx={{ width: 34, height: 34, bgcolor: blue[700] }}>
                                <Article />
                            </Avatar>
                        )}
                    </ListItemAvatar>
                    {option.kind === 'people' && (
                        <ListItemText 
                            primary={option.email}
                            primaryTypographyProps={{ variant: 'h6' }}
                        />
                    )}
                    {option.kind === 'message' && (
                        <ListItemText 
                            primary={option.title}
                            primaryTypographyProps={{ variant: 'h6' }}
                        />
                    )}
                    {option.kind === 'file' && (
                        <ListItemText 
                            primary={option.name}
                            primaryTypographyProps={{ variant: 'h6' }}
                        />
                    )}
                </ListItem>
            )}
            renderTags={(value, getTagProps) => 
                value?.map((option, index) => (
                    <Chip 
                        avatar={
                            option.kind === 'people' ? <Avatar src={option.photo} /> : 
                            option.kind === 'message' ? <Avatar><Message /></Avatar> :
                            option.kind === 'file' ? <Avatar><Article /></Avatar> :
                            null
                        }
                        variant="outlined"
                        label={
                            <Typography 
                                variant="h6"
                                whiteSpace="nowrap"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                width="100vw"
                            >
                                {
                                    option.kind === 'people' ? option.email :
                                    option.kind === 'message' ? option.title :
                                    option.kind === 'file' ? option.name :
                                    'undefined'
                                }
                            </Typography>
                        }
                        {...getTagProps({ index })} 
                    />
                ))
            }
        />
    );
};

export default HomePageAutocomplete;